import { supabaseAdmin } from '../supabase.js';

class TeacherManagementService {
  constructor(tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3') {
    this.tenantId = tenantId;
  }

  // =====================================================
  // SUBJECT TEACHER MANAGEMENT
  // =====================================================

  // Add teacher to subject with role
  async addTeacherToSubject(teacherId, subjectName, role = '') {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .insert({
          tenant_id: this.tenantId,
          teacher_id: teacherId,
          class_id: null,
          assignment_type: 'subject_teacher',
          subject_name: subjectName,
          member_role: role,
          active_from: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();

      if (error) {
        console.error('Error adding teacher to subject:', error);
        return { success: false, error };
      }

      return { success: true, data: data[0] };
    } catch (error) {
      console.error('Error in addTeacherToSubject:', error);
      return { success: false, error };
    }
  }

  // Remove teacher from subject
  async removeTeacherFromSubject(teacherId, subjectName) {
    try {
      const { error } = await supabaseAdmin
        .from('teacher_assignments')
        .delete()
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'subject_teacher')
        .eq('subject_name', subjectName);

      if (error) {
        console.error('Error removing teacher from subject:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in removeTeacherFromSubject:', error);
      return { success: false, error };
    }
  }

  // Update teacher's subject assignment
  async updateTeacherSubject(teacherId, oldSubjectName, newSubjectName) {
    try {
      const { error } = await supabaseAdmin
        .from('teacher_assignments')
        .update({
          subject_name: newSubjectName,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'subject_teacher')
        .eq('subject_name', oldSubjectName);

      if (error) {
        console.error('Error updating teacher subject:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateTeacherSubject:', error);
      return { success: false, error };
    }
  }

  // Update teacher's role in subject
  async updateTeacherSubjectRole(teacherId, subjectName, newRole) {
    try {
      const { error } = await supabaseAdmin
        .from('teacher_assignments')
        .update({
          member_role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'subject_teacher')
        .eq('subject_name', subjectName);

      if (error) {
        console.error('Error updating teacher subject role:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateTeacherSubjectRole:', error);
      return { success: false, error };
    }
  }

  // =====================================================
  // COMMITTEE MEMBER MANAGEMENT
  // =====================================================

  // Add teacher to committee with role
  async addTeacherToCommittee(teacherId, committeeName, role = '') {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .insert({
          tenant_id: this.tenantId,
          teacher_id: teacherId,
          class_id: null,
          assignment_type: 'committee_member',
          committee_name: committeeName,
          member_role: role,
          active_from: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();

      if (error) {
        console.error('Error adding teacher to committee:', error);
        return { success: false, error };
      }

      return { success: true, data: data[0] };
    } catch (error) {
      console.error('Error in addTeacherToCommittee:', error);
      return { success: false, error };
    }
  }

  // Remove teacher from committee
  async removeTeacherFromCommittee(teacherId, committeeName) {
    try {
      const { error } = await supabaseAdmin
        .from('teacher_assignments')
        .delete()
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'committee_member')
        .eq('committee_name', committeeName);

      if (error) {
        console.error('Error removing teacher from committee:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in removeTeacherFromCommittee:', error);
      return { success: false, error };
    }
  }

  // Update teacher's role in committee
  async updateTeacherCommitteeRole(teacherId, committeeName, newRole) {
    try {
      const { error } = await supabaseAdmin
        .from('teacher_assignments')
        .update({
          member_role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'committee_member')
        .eq('committee_name', committeeName);

      if (error) {
        console.error('Error updating teacher committee role:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateTeacherCommitteeRole:', error);
      return { success: false, error };
    }
  }

  // =====================================================
  // UTILITY METHODS
  // =====================================================

  // Get all available teachers
  async getAllTeachers() {
    try {
      const { data, error } = await supabaseAdmin
        .from('saas_teachers')
        .select('id, name, teacher_id, subject_specialty')
        .eq('tenant_id', this.tenantId)
        .order('teacher_no', { ascending: true });

      if (error) {
        console.error('Error fetching teachers:', error);
        return { teachers: [], error };
      }

      return { teachers: data || [], error: null };
    } catch (error) {
      console.error('Error in getAllTeachers:', error);
      return { teachers: [], error };
    }
  }

  // Get all available subjects
  async getAllSubjects() {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .select('subject_name')
        .eq('tenant_id', this.tenantId)
        .eq('assignment_type', 'subject_teacher')
        .not('subject_name', 'is', null);

      if (error) {
        console.error('Error fetching subjects:', error);
        return { subjects: [], error };
      }

      const uniqueSubjects = [...new Set((data || []).map(item => item.subject_name))];
      return { subjects: uniqueSubjects, error: null };
    } catch (error) {
      console.error('Error in getAllSubjects:', error);
      return { subjects: [], error };
    }
  }

  // Get all available committees
  async getAllCommittees() {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .select('committee_name')
        .eq('tenant_id', this.tenantId)
        .eq('assignment_type', 'committee_member')
        .not('committee_name', 'is', null);

      if (error) {
        console.error('Error fetching committees:', error);
        return { committees: [], error };
      }

      const uniqueCommittees = [...new Set((data || []).map(item => item.committee_name))];
      return { committees: uniqueCommittees, error: null };
    } catch (error) {
      console.error('Error in getAllCommittees:', error);
      return { committees: [], error };
    }
  }

  // Check if teacher is already assigned to subject
  async isTeacherInSubject(teacherId, subjectName) {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .select('id')
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'subject_teacher')
        .eq('subject_name', subjectName);

      if (error) {
        console.error('Error checking teacher in subject:', error);
        return false;
      }

      return (data || []).length > 0;
    } catch (error) {
      console.error('Error in isTeacherInSubject:', error);
      return false;
    }
  }

  // Check if teacher is already assigned to committee
  async isTeacherInCommittee(teacherId, committeeName) {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .select('id')
        .eq('tenant_id', this.tenantId)
        .eq('teacher_id', teacherId)
        .eq('assignment_type', 'committee_member')
        .eq('committee_name', committeeName);

      if (error) {
        console.error('Error checking teacher in committee:', error);
        return false;
      }

      return (data || []).length > 0;
    } catch (error) {
      console.error('Error in isTeacherInCommittee:', error);
      return false;
    }
  }
}

export default TeacherManagementService;
